import { LogManager } from '@src/utility/Logger';

import { SceneService } from '../../services/SceneService';
import { PresentationTimeFrameConversionService } from '../../services/timeline/PresentationTimeFrameConversionService';
import { KARAOKE_TRACK_NUMBER, LAYOUT_TRACKS_BASE } from '../constants';
import { applyDubToDocument } from '../dubbing/applyDubToDocument';
import { normalizeDubVoiceVolume } from '../dubbing/DubEngine';
import { isDubAudioSource, isMusicBedSource } from '../dubbing/dubSources';
import { holdDubLeadIn } from '../dubbing/leadIn';
import { holdDubPerLanguageConfiguration } from '../dubbing/segmentLevels';
import { serializeElement as serializeFadeElement } from '../serialization/FadeAnimationSerializer';
import { convertFromPixels } from '../services/utils';
import { Element } from './Element';
import { AudioElement } from './elements/AudioElement';
import { CompositionElement } from './elements/CompositionElement';
import { ImageElement } from './elements/ImageElement';
import { ShapeElement } from './elements/ShapeElement';
import { TextElement } from './elements/TextElement';
import { VideoElement } from './elements/VideoElement';
import { KaraokeClip } from './karaoke/KaraokeClip';
import { KaraokeTrack } from './karaoke/KaraokeTrack';
import { Scene } from './layout';
import { LayoutEngine } from './layout/LayoutEngine';
import { Track } from './Track';

import type { DubSource } from '../dubbing/DubDescriptionStore';
import type { DubMusicMode, DubVoiceMode } from '../dubbing/DubEngine';
import type { DubLeadIn } from '../dubbing/leadIn';
import type { DubPerLanguageConfiguration } from '../dubbing/segmentLevels';
import type { TrackType } from '../types/TrackType';
import type { LayoutOptions, LayoutRepresentationMap } from './layout/types';

export { KARAOKE_TRACK_NUMBER };

const logger = LogManager.getLogger('Timeline');

const TIMESTAMP_PRECISION = 4;

/**
 * Type for element JSON with track property
 */
interface ElementJSON extends Record<string, unknown> {
  track: number;
}

type CustomFont = {
  family: string;
  weight: number;
  style: string;
  source: string;
};

/**
 * The values a clip stores about its dub (PLAT-11511).
 *
 * Everything else about the dub is derived: the audio itself is worked out by
 * the dub engine every time the timeline is turned into a document, so it can
 * never drift out of step with a picture that has been trimmed or cut. These
 * are the only part a person chose and nothing can recompute.
 *
 * Two levels, per clip and not per language, so switching the dub's language
 * keeps them: the dubbed voice's (PLAT-11899) and, on Both, the picture's
 * (PLAT-11927). The music bed still has no level of its own.
 */
export interface DubOptions {
  /**
   * The dub's target language tag, for example `ru-RU`, or `null` when nobody
   * has used the Voice control on this clip. `null` is a distinct state, not a
   * spelling of the original voice: a clip with no chosen position is published
   * differently from one whose person chose the original.
   */
  language: string | null;
  /**
   * Which voice is heard. `original` means the dub is off and the recording's
   * own sound plays; `dubbed` means the dubbed voice plays over a silenced
   * picture; `both` means the dubbed voice plays and the picture keeps its own
   * sound, at `originalVolume`.
   */
  voiceMode: DubVoiceMode;
  /** Whether the recording's own separated music plays under the dub. */
  musicMode: DubMusicMode;
  /**
   * The dubbed voice's level, a whole percentage from 0 to 99. Absent means
   * full (100) — and full is never written, so a clip nobody turned down keeps
   * exactly the stored shape it had before this existed. See
   * `normalizeDubVoiceVolume`.
   */
  voiceVolume?: number;
  /**
   * PLAT-11927 — the original recording's level under the dub on Both, a whole
   * percentage from 0 to 99, held to the same rule as `voiceVolume`: absent
   * means full and full is never written. Read on Both only; Dubbed silences
   * the picture and Original leaves it at the person's own level.
   */
  originalVolume?: number;
  /**
   * What each dubbed language holds of its own, by `normalizeLangKey`: its
   * lines played at their own levels instead of the clip's. Absent when no
   * language holds anything, and never written empty — the same hash rule as
   * the levels above. See `segmentLevels.ts`.
   */
  perLanguageConfiguration?: DubPerLanguageConfiguration;
  /**
   * The original lead-in on Both: the clip opens with the original alone and
   * each line's dub comes in this long after its line starts. Absent when off,
   * and never written off — the same hash rule as the levels above. See
   * `leadIn.ts`.
   */
  leadIn?: DubLeadIn;
}

/**
 * Has nobody chosen anything about this clip's dub?
 *
 * Load-bearing, and not merely tidiness. The publish staleness check hashes the
 * whole stored timeline, so a key that is always there — even holding these
 * defaults — would move that hash for every clip in the library and mark every
 * already-published render as outdated on its first save.
 */
export function dubOptionsAreEmpty(options: DubOptions | null): boolean {
  if (!options) return true;
  return (
    (options.language ?? null) === null &&
    options.voiceMode === 'original' &&
    options.musicMode === 'none' &&
    normalizeDubVoiceVolume(options.voiceVolume) === undefined &&
    normalizeDubVoiceVolume(options.originalVolume) === undefined &&
    holdDubPerLanguageConfiguration(options.perLanguageConfiguration) ===
      undefined &&
    holdDubLeadIn(options.leadIn) === undefined
  );
}

/**
 * The options as they are held: a level at full carries no key at all, for the
 * same hash reason as `dubOptionsAreEmpty`.
 */
function holdDubOptions(options: DubOptions): DubOptions {
  const {
    voiceVolume,
    originalVolume,
    perLanguageConfiguration,
    leadIn,
    ...rest
  } = options;
  const held: DubOptions = { ...rest };
  const voiceLevel = normalizeDubVoiceVolume(voiceVolume);
  if (voiceLevel !== undefined) held.voiceVolume = voiceLevel;
  const originalLevel = normalizeDubVoiceVolume(originalVolume);
  if (originalLevel !== undefined) held.originalVolume = originalLevel;
  const perLanguage = holdDubPerLanguageConfiguration(perLanguageConfiguration);
  if (perLanguage) held.perLanguageConfiguration = perLanguage;
  const heldLeadIn = holdDubLeadIn(leadIn);
  if (heldLeadIn) held.leadIn = heldLeadIn;
  return held;
}

/**
 * The stored options as read back from a file, or `undefined`.
 *
 * Deliberately tolerant. A stored file's value can hold anything, this runs on
 * every load including the publish path, and reading must never be what turns a
 * malformed file into a crash. Anything unrecognised falls back to the default
 * for that value, which is the clip playing its own recording.
 */
function readDubOptions(raw: unknown): DubOptions | undefined {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const held = raw as Partial<DubOptions>;
  const language = typeof held.language === 'string' ? held.language : null;
  const voiceMode: DubVoiceMode =
    held.voiceMode === 'dubbed' || held.voiceMode === 'both'
      ? held.voiceMode
      : 'original';
  const musicMode: DubMusicMode =
    held.musicMode === 'original' ? 'original' : 'none';
  const options = holdDubOptions({
    language,
    voiceMode,
    musicMode,
    voiceVolume: held.voiceVolume,
    originalVolume: held.originalVolume,
    perLanguageConfiguration: held.perLanguageConfiguration,
    leadIn: held.leadIn,
  });
  return dubOptionsAreEmpty(options) ? undefined : options;
}

/**
 * Is this element dub audio — the dubbed voice, or the recording's own
 * separated music laid under it?
 */
function isDubAudioElement(element: Element): boolean {
  if (!(element instanceof AudioElement)) return false;
  return isDubAudioSource(element.source) || isMusicBedSource(element.source);
}

export type CoupledElementBounds = Record<
  string,
  { time: number; duration: number }
>;

interface TimelineTempEnhancements {
  layoutRepresentationMap?: LayoutRepresentationMap;
  coupledElementBounds?: CoupledElementBounds;
  [key: string]: unknown;
}

/**
 * Type for JSON serialization of timeline data
 */
export interface TimelineJSON {
  width?: number;
  height?: number;
  elements?: Record<string, unknown>[];
  frame_rate?: number;
  output_format?: string;
  duration?: number | null;
  snapToFrame?: boolean;
  fonts?: CustomFont[];
  dubOptions?: DubOptions;
  tempEnhancements?: TimelineTempEnhancements | null;
  [key: string]: unknown;
}

/**
 * Type for element constructor properties
 */
interface ElementProps {
  id?: string;
  type?: string;
  track?: number | null;
  time?: number;
  duration?: number | string | null;
  [key: string]: unknown;
}

export const DEFAULT_INIT_FRAMERATE = 24;

/**
 * Timeline represents the entire project timeline containing tracks and metadata
 * Aligned with Creatomate properties and structure
 */
export class Timeline {
  // Private properties with readonly public access
  private _id: string = `timeline_${Date.now()}_${Math.random()
    .toString(36)
    .substring(2, 11)}`;

  private _tracks: Map<string, Track> = new Map();

  private _width: number = 1920;

  private _height: number = 1080;

  private _duration: number | null = null;

  private _framerate: number;

  private _snapToFrame: boolean = true;

  private _fonts: CustomFont[] | null = null;

  private _dubOptions: DubOptions | null = null;

  private _scenes: Scene[] = [];

  private _trackIdMap: Map<number, number> | null = null;

  /**
   * Where the dub engine reads the resolved dub description from, or `null` on
   * a timeline nobody has attached one to — a fixture, or a throwaway built for
   * one render. A `null` source places no dub audio and changes nothing, which
   * is what every timeline that has never seen a dub should get.
   *
   * Held rather than owned, because loading a story replaces the timeline while
   * the descriptions in hand are still the right ones; the editor owns the
   * holder and re-attaches it.
   */
  private _dubSource: DubSource | null = null;

  constructor(props: Partial<Timeline> = {}) {
    // Initialize the tracks map
    if (props.tracks) {
      this._tracks = new Map(props.tracks.map(track => [track.id, track]));
    }
    this._width = props.width ?? 1920;
    this._height = props.height ?? 1080;
    this._duration = props.duration ?? null;
    this._framerate = props.framerate ?? DEFAULT_INIT_FRAMERATE;
    this._snapToFrame = props.snapToFrame ?? true;
    this._fonts = props.fonts ?? null;
    // Copied, never adopted: the caller's object is usually the stored clip the
    // store keeps holding, and the model must not be editable through it.
    //
    // Held to the same emptiness rule as `setDubOptions`, so a value that says
    // nothing was chosen reads as nothing chosen however it arrived.
    this._dubOptions =
      props.dubOptions && !dubOptionsAreEmpty(props.dubOptions)
        ? holdDubOptions(props.dubOptions)
        : null;
    this._trackIdMap = props.trackIdMap ?? null;

    // Calculate duration from elements if not provided
    if (this._duration === null) {
      this.recalculateDuration();
    }
  }

  // Getters for private properties
  get id(): string {
    return this._id;
  }

  get tracks(): readonly Track[] {
    return Array.from(this._tracks.values());
  }

  get tracksMap(): ReadonlyMap<string, Track> {
    return this._tracks;
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get duration(): number {
    return this._duration ?? 0;
  }

  get framerate(): number {
    return this._framerate;
  }

  get snapToFrame(): boolean {
    return this._snapToFrame;
  }

  get fonts(): CustomFont[] | null {
    return this._fonts;
  }

  /**
   * The three values this clip stores about its dub, or `null` when nobody has
   * used the Voice control on it.
   *
   * A copy, on purpose: the model's own object never leaves the model, so no
   * caller can change the clip's dub by reaching through this getter.
   */
  get dubOptions(): DubOptions | null {
    return this._dubOptions ? { ...this._dubOptions } : null;
  }

  /**
   * Write the clip's dub options.
   *
   * Nothing is stored when every value is at its default — see
   * `dubOptionsAreEmpty` for why a key that is always present would mark every
   * already-published render in the library as outdated.
   */
  setDubOptions(options: DubOptions | null): void {
    this._dubOptions =
      options && !dubOptionsAreEmpty(options) ? holdDubOptions(options) : null;
  }

  /** Give this timeline the editor's dub holder to read from. */
  attachDubSource(source: DubSource | null): void {
    this._dubSource = source;
  }

  get dubSource(): DubSource | null {
    return this._dubSource;
  }

  get trackIdMap(): Map<number, number> | null {
    return this._trackIdMap;
  }

  /**
   * Remaps an old track ID to its new ID after track normalization.
   * Returns the original ID if no mapping exists.
   */
  remapTrackId(oldId: number): number {
    return this._trackIdMap?.get(oldId) ?? oldId;
  }

  get scenes(): readonly Scene[] {
    return this._scenes;
  }

  findSceneById(sceneId: string): Scene | undefined {
    return this.scenes.find(scene => scene.id === sceneId);
  }

  // Public setters as methods (for MobX actions and validation)
  setWidth(width: number): void {
    if (width <= 0) {
      throw new Error('Width must be greater than 0');
    }
    this._width = width;
  }

  setHeight(height: number): void {
    if (height <= 0) {
      throw new Error('Height must be greater than 0');
    }
    this._height = height;
  }

  setDuration(duration: number | null): void {
    if (duration !== null && duration < 0) {
      throw new Error('Duration cannot be negative');
    }
    this._duration = duration;
  }

  setFramerate(value: number): void {
    // Validate that the value is one of the supported framerates
    if (value < 1 || value > 60) {
      throw new Error(`Invalid framerate: ${value}. Must be between 1 and 60.`);
    }
    this._framerate = value;
  }

  setSnapToFrame(value: boolean): void {
    this._snapToFrame = value;
  }

  setScenes(scenes: Scene[]): void {
    this._scenes = scenes;
  }

  /**
   * Remaps scene layout binding track IDs using a mapping table in a single pass (supports swaps and cycles).
   */
  remapSceneBindings(mapping: Map<string, string>): void {
    if (mapping.size === 0) {
      return;
    }

    this._scenes = this._scenes.map(scene => {
      let updated = false;
      const layout = scene.layout.clone();

      Object.entries(layout.bindings).forEach(([slot, transforms]) => {
        const targetTrackId = mapping.get(transforms.trackId);
        if (targetTrackId && targetTrackId !== transforms.trackId) {
          layout.bindTrack(slot, { ...transforms, trackId: targetTrackId });
          updated = true;
        }
      });

      if (!updated) {
        return scene;
      }

      return new Scene({
        id: scene.id,
        anchorStart: scene.anchorStart,
        anchorEnd: scene.anchorEnd,
        layout,
      });
    });
  }

  /**
   * Moves a track to a new ID, remapping every timeline reference to it (scene
   * bindings, coupled logos). Caller updates element.track values first.
   */
  moveTrackId(track: Track, oldId: string, newId: string): void {
    if (oldId === newId) {
      return;
    }

    if (!this._tracks.has(oldId)) {
      throw new Error(`Cannot move missing track ${oldId}`);
    }

    if (this._tracks.has(newId)) {
      throw new Error(`Cannot move track to existing ID ${newId}`);
    }

    this._tracks.delete(oldId);
    track.setId(newId);
    this._tracks.set(newId, track);

    const mapping = new Map([[oldId, newId]]);
    this.remapSceneBindings(mapping);
    this.remapCoupledTrackIds(mapping);
  }

  /**
   * Swaps two tracks' IDs, remapping every timeline reference to them (scene
   * bindings, coupled logos). Caller updates element.track on both tracks first.
   */
  swapTrackIds(trackA: Track, trackB: Track, idA: string, idB: string): void {
    if (idA === idB) {
      return;
    }

    if (!this._tracks.has(idA) || !this._tracks.has(idB)) {
      throw new Error(`Cannot swap missing tracks ${idA} and ${idB}`);
    }

    this._tracks.delete(idA);
    this._tracks.delete(idB);

    trackA.setId(idB);
    trackB.setId(idA);

    this._tracks.set(idB, trackA);
    this._tracks.set(idA, trackB);

    const mapping = new Map([
      [idA, idB],
      [idB, idA],
    ]);
    this.remapSceneBindings(mapping);
    this.remapCoupledTrackIds(mapping);
  }

  /**
   * Remaps the track a coupled logo follows, in a single pass (supports swaps).
   *
   * A coupled logo names its track by id, so a track that changes id without
   * this leaves the logo following whatever track takes the old id — adding
   * music shifts every track up and the logo then spans the music, not the
   * transcript track, and stops following transcript cuts.
   */
  private remapCoupledTrackIds(mapping: Map<string, string>): void {
    for (const el of this.allElements) {
      if (!Element.isLogoElement(el) || !el.isCoupledToTrack()) continue;
      const targetTrackId = mapping.get(el.coupledTrackId!);
      if (targetTrackId) el.coupledTrackId = targetTrackId;
    }
  }

  /**
   * Gets all elements across all tracks
   */
  get allElements(): readonly Element[] {
    const elements = Array.from(this._tracks.values()).flatMap(
      track => track.elements,
    );

    // We don't modify the array in place, but we need to make it sortable for tests
    // This adds a 'sort' method to the returned array
    Object.defineProperty(elements, 'sort', {
      value(compareFn: (a: Element, b: Element) => number): Element[] {
        return [...elements].sort(compareFn);
      },
      enumerable: false,
    });

    return elements;
  }

  /**
   * Creates a new track and adds it to the timeline
   */
  createTrack(
    type: TrackType = 'video',
    trackIdOverride?: string, // for cases when track doesn't follow an order, example: stacked layout
  ): Track {
    const trackId = trackIdOverride ?? String(this.getNextTrackId());
    const track = new Track({ id: trackId, type });

    // Add to the tracks map
    this._tracks.set(trackId, track);

    return track;
  }

  /**
   * Adds a track with given properties to the timeline (for compatibility with tests)
   */
  addTrack(props: { name: string; type?: TrackType }): string {
    const type = props.type || 'video';
    const track = this.createTrack(type);
    return track.id;
  }

  /**
   * Adds an element to a track (for compatibility with tests)
   * TODO: Remove this and prefer object based approach?
   */
  addElementToTrack(trackId: string, element: Element): boolean {
    return this.addElement(trackId, element);
  }

  /**
   * Gets the next available track ID
   * Track IDs must be below 32 as 32+ are reserved for internal work
   */
  private getNextTrackId(): number {
    if (this._tracks.size === 0) {
      return 1;
    }

    // Convert string IDs to numbers, filter out reserved IDs (32+), and get available IDs
    const trackIds = Array.from(this._tracks.keys())
      .map(id => Number(id))
      .filter(id => !Number.isNaN(id) && id < KARAOKE_TRACK_NUMBER); // Only consider valid IDs below 32

    // Find the first available ID from 1 to 31
    for (let i = 1; i < KARAOKE_TRACK_NUMBER; i += 1) {
      if (!trackIds.includes(i)) {
        return i;
      }
    }

    // If all IDs from 1-31 are taken, throw an error
    throw new Error(
      'No available track IDs below 32. Maximum number of tracks (31) reached.',
    );
  }

  /**
   * Adds an existing Track instance to the timeline
   * @param track The pre-configured Track instance to add
   * @returns The added track for method chaining
   * @throws Error if a track with the same ID already exists
   */
  addExistingTrack(track: Track): Track {
    // Validate that the track ID is valid
    const trackIdNum = Number(track.id);
    if (
      trackIdNum === KARAOKE_TRACK_NUMBER &&
      !(track instanceof KaraokeTrack)
    ) {
      throw new Error(
        `Track ID ${KARAOKE_TRACK_NUMBER} is reserved for karaoke tracks.`,
      );
    }

    if (Number.isNaN(trackIdNum) || trackIdNum < 1 || trackIdNum > 32) {
      throw new Error(
        `Invalid track ID: ${track.id}. Track IDs must be between 1 and 32.`,
      );
    }

    // Check if a track with this ID already exists
    if (this._tracks.has(track.id)) {
      throw new Error(`A track with ID ${track.id} already exists.`);
    }

    // Add the track to the map
    this._tracks.set(track.id, track);

    // Recalculate duration since we added a track that might have elements
    this.recalculateDuration();

    return track;
  }

  /**
   * Adds an element to a track
   */
  addElement(trackId: string, element: Element): boolean {
    const track = this.getTrack(trackId);
    if (!track) return false;

    track.addElement(element);
    this.recalculateDuration();
    return true;
  }

  /**
   * Updates an element's properties
   */
  updateElement(elementId: string, props: Partial<Element>): boolean {
    let success = false;

    for (const track of this._tracks.values()) {
      if (track.updateElement(elementId, props)) {
        success = true;
        break;
      }
    }

    if (success) this.recalculateDuration();
    return success;
  }

  /**
   * Removes an element from the timeline
   */
  removeElement(elementId: string): boolean {
    let success = false;

    for (const track of this._tracks.values()) {
      if (track.removeElement(elementId)) {
        success = true;
        break;
      }
    }

    if (success) this.recalculateDuration();
    return success;
  }

  /**
   * Replaces an element with a new element while preserving position and track
   * This is used for element type transitions (e.g., ImageElement ↔ CompositionElement)
   */
  replaceElement(elementId: string, newElement: Element): boolean {
    let success = false;

    for (const track of this._tracks.values()) {
      if (track.replaceElement(elementId, newElement)) {
        success = true;
        break;
      }
    }

    if (success) this.recalculateDuration();
    return success;
  }

  /**
   * Recalculates the timeline duration based on the elements
   * Adjusts the timeline length based on the furthest element end time
   * Excludes coupled logos from the calculation since they will be adjusted to
   * match their coupled track
   */
  recalculateDuration(): void {
    // Filter out coupled logos - they don't contribute to duration since they
    // follow their coupled track
    // Filter out karaoke track elements - their durations are recalculated from transcript track
    // Filter out dub audio: the dub is a re-voicing of the picture that is
    // already there, so it must never make the clip LONGER. A dubbed line that
    // runs past the last frame would otherwise stretch the clip and leave a
    // tail of sound over nothing.
    //
    // Recognised by the address each element plays from, not by a stored list
    // of identifiers (PLAT-11511). Nothing on the timeline names the dub's
    // elements any more, and nothing writes dub audio onto a stored timeline
    // either, so on every clip the app can now save this filter matches
    // nothing. It is kept as the guard for a stored clip that carries dub
    // audio from some other route: the address is the only thing left that
    // could recognise one, and a dubbed line running past the last frame would
    // otherwise stretch the clip and leave a tail of sound over nothing.
    const elementsForDuration = this.allElements.filter(
      el =>
        !(Element.isLogoElement(el) && el.isCoupledToTrack()) &&
        el.track !== KARAOKE_TRACK_NUMBER &&
        !isDubAudioElement(el),
    );

    if (elementsForDuration.length === 0) {
      this._duration = null;
      return;
    }

    // Calculate the end time of each element (time + duration)
    const endTimes = elementsForDuration.map(el => {
      const time = el.time || 0;
      // Handle different duration types, ensuring we get a valid number
      let duration = 0;
      if (typeof el.duration === 'number') {
        duration = el.duration;
      } else if (el.duration === 'media') {
        // For 'media' duration, we'll default to 0 for now since metadata system was removed
        const mediaDuration = 0;
        duration = mediaDuration;
      }
      return time + duration;
    });

    // Find the furthest end time, ensuring a minimum duration
    const lastEndTime = Math.max(...endTimes, 0);

    // Round to the nearest 0.0001 seconds to avoid tiny decimal differences
    this._duration = Math.ceil(lastEndTime * 10000) / 10000;
  }

  /**
   * Applies track renumbering to ensure tracks start from 1 while preserving karaoke tracks
   * Returns both the renumbered elements and a mapping of old track IDs to new track IDs
   */
  private static applyTrackRenumbering(elements: Record<string, unknown>[]): {
    elements: Record<string, unknown>[];
    trackIdMap: Map<number, number>;
  } {
    // Type guard to check if element has track property
    const hasTrackProperty = (el: Record<string, unknown>): el is ElementJSON =>
      typeof el.track === 'number';

    // Filter elements that have track property and are not karaoke elements
    const elementsWithTrack = elements.filter(hasTrackProperty);
    const nonKaraokeElements = elementsWithTrack.filter(
      el => el.track !== KARAOKE_TRACK_NUMBER,
    );

    const trackIdMap = new Map<number, number>();

    // If there are no non-karaoke elements, no adjustment needed
    if (nonKaraokeElements.length === 0) {
      return { elements, trackIdMap };
    }

    const minTrackNumber = Math.min(...nonKaraokeElements.map(el => el.track));

    if (minTrackNumber > 1) {
      const shift = minTrackNumber - 1;

      return {
        elements: elements.map(el => {
          if (hasTrackProperty(el) && el.track !== KARAOKE_TRACK_NUMBER) {
            const newTrack = el.track - shift;
            trackIdMap.set(el.track, newTrack);
            return { ...el, track: newTrack };
          }
          return el;
        }),
        trackIdMap,
      };
    }

    return { elements, trackIdMap };
  }

  /**
   * Compresses (normalizes) imported track numbers so non-karaoke tracks become
   * a contiguous ascending sequence starting at the smallest existing non-karaoke
   * track number, never shifting numbers below that minimum. Karaoke track (32)
   * and any hidden tracks (>=32) are preserved.
   *
   * Example: [2, 5, 15, 32] -> [2, 3, 4, 32]
   * Example: [1, 3, 5, 22, 32] -> [1, 2, 3, 4, 32]
   * If the set is already contiguous from its own minimum (e.g. [4,5,6]) no change.
   * Returns both the compressed elements and a mapping of old track IDs to new track IDs
   */
  private static compressImportedTrackNumbers(
    elements: Record<string, unknown>[],
  ): { elements: Record<string, unknown>[]; trackIdMap: Map<number, number> } {
    // Collect distinct non-karaoke valid numeric track IDs
    const trackSet = new Set<number>();
    let hasMissingTrack = false;
    for (const el of elements) {
      const t = typeof el.track === 'number' ? el.track : undefined;
      if (
        typeof t === 'number' &&
        !Number.isNaN(t) &&
        t >= 1 &&
        t < KARAOKE_TRACK_NUMBER // exclude karaoke track 32
      ) {
        trackSet.add(t);
      } else if (el.track === null || typeof el.track === 'undefined') {
        // Mark presence of missing track so we can seed track 1 for compression
        hasMissingTrack = true;
      }
    }

    // If there are elements with missing track and no explicit track 1 present, seed 1 so compression starts at 1
    if (hasMissingTrack && !trackSet.has(1)) {
      trackSet.add(1);
    }

    // If zero or one track or already contiguous from its own minimum -> nothing to do
    const sorted = Array.from(trackSet).sort((a, b) => a - b);
    const minTrack = sorted[0];
    if (
      sorted.length <= 1 ||
      sorted.every((id, idx) => id === minTrack + idx)
    ) {
      return { elements, trackIdMap: new Map() };
    }

    // Build mapping from old track id -> new contiguous id starting at original minimum
    const idMap = new Map<number, number>();
    sorted.forEach((oldId, idx) => {
      const newId = minTrack + idx; // start at smallest existing
      // Avoid mapping into karaoke track by skipping adjustment if newId === KARAOKE_TRACK_NUMBER
      if (newId === KARAOKE_TRACK_NUMBER) {
        idMap.set(oldId, oldId); // preserve original to avoid collision
      } else {
        idMap.set(oldId, newId);
      }
    });

    // Apply mapping, preserving karaoke (32) and non-numeric values
    const resultElements = elements.map(el => {
      const originalIsMissing =
        el.track === null || typeof el.track === 'undefined';
      const t =
        typeof el.track === 'number'
          ? el.track
          : originalIsMissing
            ? 1
            : undefined;
      if (
        typeof t === 'number' &&
        t !== KARAOKE_TRACK_NUMBER &&
        idMap.has(t) &&
        idMap.get(t) !== t
      ) {
        return { ...el, track: idMap.get(t) };
      }
      // Ensure missing tracks adopt normalized track id (after mapping) if they were seeded
      if (originalIsMissing && idMap.has(1)) {
        return { ...el, track: idMap.get(1) };
      }
      return el;
    });

    return { elements: resultElements, trackIdMap: idMap };
  }

  /**
   * Moves layout tracks (>= LAYOUT_TRACKS_BASE, i.e., 90+) to the bottom of the track list.
   * Layout tracks are remapped: 90 -> 1, 91 -> 2, etc.
   * All other tracks are shifted up by (number of layout tracks + 1) to make room.
   * An empty track is left as a separator between layout tracks and original tracks.
   *
   * @param elements Array of element JSON objects with track property
   * @returns Modified elements array with remapped track numbers
   */
  static moveLayoutTracksToBottom(elements: ElementJSON[]): ElementJSON[] {
    // Find all unique layout track numbers (>= LAYOUT_TRACKS_BASE)
    const layoutTrackNumbers = new Set<number>();
    for (const el of elements) {
      if (typeof el.track === 'number' && el.track >= LAYOUT_TRACKS_BASE) {
        layoutTrackNumbers.add(el.track);
      }
    }

    // If no layout tracks, return unchanged
    if (layoutTrackNumbers.size === 0) {
      return elements;
    }

    // Sort layout track numbers to create consistent mapping
    const sortedLayoutTracks = Array.from(layoutTrackNumbers).sort(
      (a, b) => a - b,
    );

    // Build mapping: 90 -> 1, 91 -> 2, etc.
    const layoutTrackMap = new Map<number, number>();
    sortedLayoutTracks.forEach((oldTrack, idx) => {
      layoutTrackMap.set(oldTrack, idx + 1);
    });

    // Calculate shift for non-layout tracks: number of layout tracks
    const shiftAmount = sortedLayoutTracks.length;

    // Remap all elements
    return elements.map(el => {
      if (typeof el.track !== 'number') {
        return el;
      }

      if (el.track >= LAYOUT_TRACKS_BASE) {
        // Layout track: remap to bottom (1, 2, 3, ...)
        const newTrack = layoutTrackMap.get(el.track);
        return { ...el, track: newTrack ?? el.track };
      } else if (el.track === KARAOKE_TRACK_NUMBER) {
        // Karaoke track: preserve hard-coded track number
        return el;
      } else {
        // Non-layout track: shift up
        return { ...el, track: el.track + shiftAmount };
      }
    });
  }

  /**
   * Assigns `z_index` to each element based on its `track` number.
   * Layout compositions (track >= 90) get z_index -1000 to stay behind everything.
   * Nested child elements also receive z_index matching their track.
   * Mutates the elements in place and returns the array for chaining.
   */
  static assignZIndexFromTrack(
    elements: Record<string, any>[],
  ): Record<string, any>[] {
    for (const el of elements) {
      if (!el || el.track === undefined) continue;
      el.z_index =
        el.type === 'composition' && el.track >= LAYOUT_TRACKS_BASE
          ? -1000
          : el.track;

      // TODO: do we need to update children z_index with their tracks or parent track?
      // if (Array.isArray(el.elements)) {
      //   for (const child of el.elements) {
      //     if (child && child.track !== undefined) {
      //       child.z_index = child.track;
      //     }
      //   }
      // }
    }
    return elements;
  }

  /**
   * Discards layout composition elements and re-numbers remaining tracks.
   *
   * Only layout-composition tracks that sit *below* the lowest non-layout
   * track cause a downward shift (i.e. tracks that were moved to the bottom
   * by `moveLayoutTracksToBottom`). Layout-composition elements on high
   * tracks (≥ LAYOUT_TRACKS_BASE) are simply removed without shifting.
   *
   * @param elements Array of element JSON objects with track property
   * @returns Modified elements array with layout tracks discarded and tracks renumbered
   */
  static discardLayoutTracks(
    elements: Record<string, unknown>[],
  ): Record<string, unknown>[] {
    if (elements.length === 0) {
      return elements;
    }

    // Partition elements into layout-composition vs non-layout and collect track info
    const layoutTracks = new Set<number>();
    let minNonLayoutTrack = Infinity;

    for (const el of elements) {
      const id = typeof el.id === 'string' ? el.id : '';
      const track = typeof el.track === 'number' ? el.track : null;

      if (id.startsWith('layout_composition')) {
        if (track !== null) {
          layoutTracks.add(track);
        }
      } else {
        if (track !== null && track < minNonLayoutTrack) {
          minNonLayoutTrack = track;
        }
      }
    }

    // If no layout tracks found, return unchanged
    if (layoutTracks.size === 0) {
      return elements;
    }

    // Only count layout tracks that sit below the lowest non-layout track
    let bottomLayoutTrackCount = 0;
    for (const track of layoutTracks) {
      if (track < minNonLayoutTrack) {
        bottomLayoutTrackCount++;
      }
    }

    // Filter out layout composition elements and shift remaining down
    return elements
      .filter(el => {
        const id = typeof el.id === 'string' ? el.id : '';
        return !id.startsWith('layout_composition');
      })
      .map(el => {
        const track = typeof el.track === 'number' ? el.track : 1;
        if (track === KARAOKE_TRACK_NUMBER) {
          return el;
        }
        return { ...el, track: track - bottomLayoutTrackCount };
      });
  }

  /**
   * Normalizes imported track numbers: first compresses to remove gaps, then shifts to start at 1.
   * Returns normalized elements and a combined track ID mapping.
   */
  private static normalizeImportedTrackNumbers(
    elements: Record<string, unknown>[],
  ): {
    elements: Record<string, unknown>[];
    trackIdMap: Map<number, number>;
  } {
    // First compress gaps
    const compressResult = Timeline.compressImportedTrackNumbers(elements);
    // Then shift to start at 1
    const renumberResult = Timeline.applyTrackRenumbering(
      compressResult.elements,
    );

    // Keyed by imported ids. The renumber map is keyed by compressed ids, so it
    // can only be used as-is when compression was a no-op; otherwise compose.
    // Compression maps every non-karaoke track, so its keys are complete.
    const combinedTrackIdMap = new Map<number, number>();
    if (compressResult.trackIdMap.size === 0) {
      renumberResult.trackIdMap.forEach((finalId, originalId) =>
        combinedTrackIdMap.set(originalId, finalId),
      );
    } else {
      compressResult.trackIdMap.forEach((compressedId, originalId) => {
        const finalId =
          renumberResult.trackIdMap.get(compressedId) ?? compressedId;
        if (finalId !== originalId) {
          combinedTrackIdMap.set(originalId, finalId);
        }
      });
    }

    return {
      elements: renumberResult.elements,
      trackIdMap: combinedTrackIdMap,
    };
  }

  /**
   * Adjusts scenes, remapping binding track IDs when track numbering changes (shifts, gap removal).
   */
  adjustScenesAfterTrackRenumber(): void {
    const trackIdMap = this._trackIdMap;

    if (!trackIdMap || trackIdMap.size === 0 || this._scenes.length === 0) {
      return;
    }

    const sceneMap = new Map<string, string>();
    trackIdMap.forEach((newId, oldId) => {
      sceneMap.set(String(oldId), String(newId));
    });

    const hasSceneBindingsToRemap = this._scenes.some(scene =>
      Object.values(scene.layout.bindings).some(binding =>
        sceneMap.has(binding.trackId),
      ),
    );

    if (!hasSceneBindingsToRemap) {
      return;
    }

    this.remapSceneBindings(sceneMap);
  }

  // ---------------------------------------------------------------------------
  // Shared helpers
  // ---------------------------------------------------------------------------

  /**
   * Converts text font-size values from px to vh and assembles the base
   * TimelineJSON structure.  Used by all serialisation paths.
   */
  private buildTimelineJSON(
    elements: Record<string, any>[],
    layoutRepresentationMap?: LayoutRepresentationMap,
    frameRateOverride?: number,
    coupledElementBounds?: CoupledElementBounds,
  ): TimelineJSON {
    // Convert font sizes from px to vh
    // TODO: temporary, find a proper place to do this!! needed for karaoke track but requires width and height
    elements.forEach(el => {
      if (el.type === 'text' && typeof el.font_size === 'string') {
        let fontSize = parseFloat(el.font_size);
        let fontSizeUnits = el.font_size.match(/[a-z]+/)?.[0] || 'px';

        if (fontSizeUnits === 'px') {
          fontSize = convertFromPixels(fontSize, 'vh', {
            width: this._width,
            height: this._height,
          });
          fontSizeUnits = 'vh';
        }
        el.font_size = `${fontSize} ${fontSizeUnits}`;
      }
    });

    const timelineJson: TimelineJSON = {
      width: this._width,
      height: this._height,
      duration: this._duration,
      elements,
      frame_rate: frameRateOverride ?? this.framerate,
      output_format: 'mp4',
      fonts: this._fonts ?? undefined,
    };

    if (layoutRepresentationMap) {
      const hasBindings = Object.values(layoutRepresentationMap).some(
        bindings => Array.isArray(bindings) && bindings.length > 0,
      );
      if (hasBindings) {
        timelineJson.tempEnhancements = {
          ...(timelineJson.tempEnhancements ?? {}),
          layoutRepresentationMap,
        };
      }
    }

    if (coupledElementBounds && Object.keys(coupledElementBounds).length > 0) {
      timelineJson.tempEnhancements = {
        ...(timelineJson.tempEnhancements ?? {}),
        coupledElementBounds,
      };
    }

    return timelineJson;
  }

  /**
   * An element's JSON for the renderer. Caption clips size their line breaks
   * to this canvas (`KaraokeClip.toRenderJSON`); the stored JSON never
   * carries them.
   */
  private elementToRenderJSON(
    element: Element,
    formatTime: Boolean,
  ): Record<string, any> {
    return element instanceof KaraokeClip
      ? element.toRenderJSON(formatTime, {
          width: this._width,
          height: this._height,
        })
      : element.toJSON(formatTime);
  }

  /**
   * Put this clip's dub into the document being produced.
   *
   * Called from BOTH serialisers, at the same point in each: after the layout
   * engine and any track reordering have run, so the dub is laid over the final
   * picture, and before the stacking order is worked out, so its own audio
   * elements are given one like everything else.
   *
   * Deliberately NOT called from the stored serialiser. The dub is a derived
   * value: putting it into what is saved is exactly the thing this change
   * exists to stop, because a stored dub keeps its old shape when the picture is
   * trimmed, and because the addresses it plays from are signed and stop being
   * served after an hour.
   */
  private applyDub(
    elements: Record<string, any>[],
    formatTime: Boolean,
  ): Record<string, any>[] {
    return applyDubToDocument({
      elements,
      timeline: this,
      options: this._dubOptions,
      source: this._dubSource,
      formatTime: formatTime === true,
    });
  }

  // ---------------------------------------------------------------------------
  // Preview serialisation (strategy-aware)
  // ---------------------------------------------------------------------------

  /**
   * Serialises the timeline for the Creatomate **preview** player.
   *
   * Accepts a `LayoutOptions` (strategy, thresholds, optional currentTime).
   * Timing adjustments are handled internally using
   * `PresentationTimeFrameConversionService.adjustTimeForPreview` (no offset,
   * truncated to 4 decimals).
   *
   * @returns The serialised JSON together with an optional `splitWindow`
   *   that the hybrid-adaptive strategy may expose for playhead tracking.
   */
  toPreviewJSON(
    layoutOptions: LayoutOptions,
    formatTime: Boolean = false,
    options?: { reorderLayoutCompositionTrackToBottom?: boolean },
  ): {
    json: TimelineJSON;
    splitWindow?: { startTime: number; endTime: number };
  } {
    const previewFrameRate =
      Math.trunc(this.framerate * 10 ** TIMESTAMP_PRECISION) /
      10 ** TIMESTAMP_PRECISION;

    const engine = new LayoutEngine();
    const timeFrameService = new PresentationTimeFrameConversionService();
    const { elements: coupledAdjusted, bounds: coupledElementBounds } =
      this.adjustCoupledElementsToTrackBounds(this.allElements);
    const layoutResult = engine.applyLayoutSegmentsToScenes(
      coupledAdjusted,
      this._scenes,
      this,
      layoutOptions,
      time => timeFrameService.adjustTimeForPreview(time, previewFrameRate),
    );

    logger.debug('[toPreviewJSON] layout applied', {
      strategy: layoutOptions.strategy,
      inputElements: this.allElements.length,
      outputElements: layoutResult.elements.length,
      scenes: this._scenes.length,
      hasSplitWindow: !!layoutResult.splitWindow,
    });

    let elements: Record<string, any>[] = layoutResult.elements
      .filter(el => el.visible)
      .map(element => this.elementToRenderJSON(element, formatTime))
      .map(json => serializeFadeElement(json));

    if (options?.reorderLayoutCompositionTrackToBottom) {
      elements = Timeline.moveLayoutTracksToBottom(elements as ElementJSON[]);
    }

    elements = this.applyDub(elements, formatTime);

    Timeline.assignZIndexFromTrack(elements);

    const timelineJson = this.buildTimelineJSON(
      elements,
      layoutResult.representationMap,
      previewFrameRate,
      coupledElementBounds,
    );

    return { json: timelineJson, splitWindow: layoutResult.splitWindow };
  }

  // ---------------------------------------------------------------------------
  // Render serialisation (always full keyframe merge)
  // ---------------------------------------------------------------------------

  /**
   * Serialises the timeline for the Creatomate **server-side render**.
   *
   * Always uses the `keyframe` strategy (full merge) and applies
   * `adjustTimeForRendering` (2% frame-boundary offset) internally so
   * that the render output matches what Creatomate expects.
   */
  toRenderJSON(
    formatTime: Boolean = false,
    options?: { reorderLayoutCompositionTrackToBottom?: boolean },
  ): TimelineJSON {
    const frameRate = this.framerate;
    const engine = new LayoutEngine();
    const timeFrameService = new PresentationTimeFrameConversionService();
    const renderOptions: LayoutOptions = {
      strategy: 'keyframe',
      sceneCount: this._scenes.length,
      sceneCountThreshold: Infinity, // always merge for render
    };
    const { elements: coupledAdjusted } =
      this.adjustCoupledElementsToTrackBounds(this.allElements);
    const layoutResult = engine.applyLayoutSegmentsToScenes(
      coupledAdjusted,
      this._scenes,
      this,
      renderOptions,
      time => timeFrameService.adjustTimeForRendering(time, frameRate),
    );

    logger.debug('[toRenderJSON] layout applied (keyframe merge)', {
      inputElements: this.allElements.length,
      outputElements: layoutResult.elements.length,
      scenes: this._scenes.length,
    });

    let elements: Record<string, any>[] = layoutResult.elements
      .map(element => this.elementToRenderJSON(element, formatTime))
      .map(json => serializeFadeElement(json));

    if (options?.reorderLayoutCompositionTrackToBottom) {
      elements = Timeline.moveLayoutTracksToBottom(elements as ElementJSON[]);
    }

    elements = this.applyDub(elements, formatTime);

    Timeline.assignZIndexFromTrack(elements);

    return this.buildTimelineJSON(elements);
  }

  // ---------------------------------------------------------------------------
  // Legacy API (deprecated — use toPreviewJSON / toRenderJSON)
  // ---------------------------------------------------------------------------

  /**
   * @deprecated Use `toPreviewJSON` for the preview player or `toRenderJSON`
   * for server-side renders.  This thin forwarder preserves backward
   * compatibility while call-sites are migrated.
   */
  toRenderedJSON(
    formatTime: Boolean = false,
    options?: { reorderLayoutCompositionTrackToBottom?: boolean },
  ): TimelineJSON {
    return this.toRenderJSON(formatTime, options);
  }

  /**
   * Serialises the timeline for persistence (saving to Dato / snapshot).
   * Does **not** run the LayoutEngine — the persisted format keeps the
   * original track numbering so data can be loaded regardless of the
   * renderer feature-flag state.
   *
   * @param formatTime Whether to format times as frame-based strings
   */
  toStoredJSON(formatTime: Boolean = false): TimelineJSON {
    const elements = this.allElements.map(element =>
      element.toJSON(formatTime),
    );
    const timelineJson = this.buildTimelineJSON(elements);

    // Attached here and deliberately NOT in buildTimelineJSON: all three
    // serialisers return that builder's result unfiltered, so a key added there
    // would leak into the screen preview and the render as well.
    //
    // Conditional on purpose. The publish staleness check hashes the whole
    // stored timeline object, so an always-present key — even an empty list —
    // would move that hash for every clip in the library and flip every
    // already-published render to "outdated" on its first save.
    //
    // A copy, never the model's own object: undo and redo keep a saved payload
    // as their snapshot without copying the video source, so a later in-place
    // edit would silently rewrite what those snapshots restore.
    if (!dubOptionsAreEmpty(this._dubOptions)) {
      timelineJson.dubOptions = { ...(this._dubOptions as DubOptions) };
    }

    return timelineJson;
  }

  /**
   * Creates a Timeline from the Creatomate's JSON state
   */
  static fromJSON(json: unknown): Timeline {
    if (!json || typeof json !== 'object' || Array.isArray(json)) {
      throw new Error('Invalid JSON: expected object');
    }

    const data = json as Record<string, unknown>;

    // Always discard layout tracks that were moved to the bottom during
    // serialization so that data persisted with the reorder flag enabled
    // can be loaded regardless of the current flag value.
    const nonLayoutElements = Timeline.discardLayoutTracks(
      Array.isArray(data.elements) ? data.elements : [],
    );

    // Normalize imported track numbering: compress gaps then shift to start at 1
    const { elements, trackIdMap } =
      Timeline.normalizeImportedTrackNumbers(nonLayoutElements);

    const elementsByTrack = new Map<number, Element[]>();

    // Group elements by track
    elements.forEach((elementJson: unknown) => {
      if (
        !elementJson ||
        typeof elementJson !== 'object' ||
        Array.isArray(elementJson)
      ) {
        return;
      }

      const elementData = elementJson as Record<string, unknown>;
      const trackId =
        typeof elementData.track === 'number' ? elementData.track : 1; // Default to track 1 if null
      if (!elementsByTrack.has(trackId)) elementsByTrack.set(trackId, []);

      let element: Element;
      const elementType =
        typeof elementData.type === 'string' ? elementData.type : 'video';
      switch (elementType) {
        case 'video':
          element = VideoElement.fromJSON(elementData);
          break;
        case 'text':
          element = TextElement.fromJSON(elementData);
          break;
        case 'audio':
          element = AudioElement.fromJSON(elementData);
          break;
        case 'image':
          element = ImageElement.fromJSON(elementData);
          break;
        case 'composition':
          element = CompositionElement.fromJSON(elementData);
          break;
        case 'shape':
          element = ShapeElement.fromJSON(elementData);
          break;
        default:
          element = Element.fromJSON(elementData);
      }
      elementsByTrack.get(trackId)!.push(element);
    });

    // Create tracks as an array first (for constructor compatibility)
    const tracks: Track[] = [];

    // Process each track
    // Skip supporting tracks (track numbers >= 90) which are internal layout helper tracks
    for (const [trackId, trackElements] of elementsByTrack.entries()) {
      if (Track.isSupporting(trackId)) {
        continue;
      }

      // Get first element's type, default to 'video' if not available
      const elementType = trackElements[0]?.type ?? 'video';
      // Ensure the type is one of the allowed types for Track
      let trackType:
        | 'video'
        | 'text'
        | 'audio'
        | 'image'
        | 'composition'
        | 'shape';

      if (Element.isImageCompositionElement(trackElements[0])) {
        // Composition elements (black-frame) should use image track type
        trackType = 'image';
      } else if (
        elementType === 'video' ||
        elementType === 'text' ||
        elementType === 'audio' ||
        elementType === 'image' ||
        elementType === 'shape' ||
        elementType === 'composition'
      ) {
        trackType = elementType as
          | 'video'
          | 'text'
          | 'audio'
          | 'image'
          | 'shape'
          | 'composition';
      } else {
        trackType = 'video';
      }

      tracks.push(
        new Track({
          id: String(trackId),
          type: trackType,
          elements: trackElements,
        }),
      );
    }

    // Read FPS and snapToFrame settings from the JSON if available
    let framerate;
    if (typeof data.frame_rate === 'number') {
      framerate = data.frame_rate;
    } else if (typeof data.frame_rate === 'string') {
      framerate = parseFloat(data.frame_rate);
    }

    if (!framerate || isNaN(framerate) || framerate > 60 || framerate < 1) {
      framerate = DEFAULT_INIT_FRAMERATE;
    }

    const width = typeof data.width === 'number' ? data.width : 1920;
    const height = typeof data.height === 'number' ? data.height : 1080;
    const duration =
      typeof data.duration === 'number' ? data.duration : undefined;
    const snapToFrame =
      typeof data.snapToFrame === 'boolean' ? data.snapToFrame : true;
    const fonts = Array.isArray(data.fonts) ? data.fonts : null;
    const dubOptions = readDubOptions(data.dubOptions);

    return new Timeline({
      tracks,
      width,
      height,
      duration,
      framerate,
      snapToFrame,
      fonts,
      dubOptions,
      trackIdMap: trackIdMap.size > 0 ? trackIdMap : null,
    });
  }

  /**
   * Helper to create a new element based on type
   */
  static createElement(type: string, props: ElementProps = {}): Element {
    switch (type) {
      case 'video':
        return new VideoElement(props);
      case 'text':
        return new TextElement(props);
      case 'audio':
        return new AudioElement(props);
      case 'image':
        return new ImageElement(props);
      case 'composition':
        return new CompositionElement(props);
      case 'shape':
        return new ShapeElement(props);
      default:
        return new Element({ ...props, type });
    }
  }

  /**
   * Removes a track by ID
   */
  removeTrack(trackId: string): boolean {
    const hasTrack = this._tracks.has(trackId);

    if (hasTrack) {
      // Don't remove scenes for karaoke track - karaoke scenes should persist
      // even when the karaoke track is removed/recreated
      if (trackId !== String(KARAOKE_TRACK_NUMBER)) {
        const sceneService = new SceneService();
        const scenesToRemove = sceneService.findScenesByTrack(this, trackId);

        scenesToRemove.forEach(scene => {
          sceneService.removeScene(this, scene.id);
        });
      }

      this._tracks.delete(trackId);
      this.recalculateDuration();
    }

    return hasTrack;
  }

  /**
   * Replaces a track with a new track, keeping the same ID
   */
  replaceTrack(trackId: string, newTrack: Track): boolean {
    const hasTrack = this._tracks.has(trackId);

    if (hasTrack) {
      this._tracks.set(trackId, newTrack);
      this.recalculateDuration();
    }

    return hasTrack;
  }

  /**
   * Gets a track by ID
   */
  getTrack(trackId: string): Track | undefined {
    return this._tracks.get(trackId);
  }

  /**
   * Gets an element by ID from any track
   */
  getElement(elementId: string): Element | undefined {
    for (const track of this._tracks.values()) {
      const element = track.elements.find(el => el.id === elementId);
      if (element) return element;
    }
    return undefined;
  }

  /**
   * Gets the track that contains the element
   */
  getTrackForElement(elementId: string): Track | undefined {
    for (const track of this._tracks.values()) {
      if (track.elements.some(element => element.id === elementId)) {
        return track;
      }
    }
    return undefined;
  }

  /**
   * Gets the track ID that contains the element
   */
  getTrackIdForElement(elementId: string): string | undefined {
    const track = this.getTrackForElement(elementId);
    return track ? track.id : undefined;
  }

  /**
   * Moves an element to a different track
   */
  moveElementToTrack(elementId: string, targetTrackId: string): boolean {
    // Find the element in any track
    const element = this.getElement(elementId);
    if (!element) return false;

    // Remove it from its current track
    if (!this.removeElement(elementId)) return false;

    // Add it to the target track
    return this.addElement(targetTrackId, element);
  }

  /**
   * Convert time in seconds to frame number
   * @param time Time in seconds
   * @returns Frame number (integer)
   */
  timeToFrame(time: number): number {
    if (time < 0) return 0;
    return Math.round(time * this._framerate);
  }

  /**
   * Convert frame number to time in seconds
   * @param frame Frame number
   * @returns Time in seconds
   */
  frameToTime(frame: number): number {
    return frame / this._framerate;
  }

  /**
   * Snap time to nearest frame boundary if enabled. This is probably a net net negative
   * as it will cause the time to be rounded to the nearest frame but Creatomate truncates
   * the time to 4 during render.
   * @param time Time in seconds
   * @returns Time in seconds, snapped to the nearest frame boundary if snapToFrame is enabled
   */
  snapTimeToFrame(time: number): number {
    if (!this._snapToFrame) return time;
    const frame = this.timeToFrame(time);
    return this.frameToTime(frame);
  }

  /**
   * Gets the first video track from the timeline, assuming there is one
   * @returns The first video track or undefined if none exists
   */
  getVideoTrack(): Track | undefined {
    for (const track of this._tracks.values()) {
      if (track.type === 'video') {
        return track;
      }
    }
    return undefined;
  }

  /**
   * Sorts the tracks array based on their numerical IDs
   * Lower IDs will appear first in the array (typically representing lower layers)
   * Higher IDs will appear later (typically representing upper layers)
   * @param descending If true, sorts in descending order (higher IDs first)
   * @returns The number of tracks that were sorted
   */
  sortTracksByNumericId(descending: boolean = false): number {
    // Convert tracks map to array for sorting
    const tracksArray = Array.from(this._tracks.values());

    if (tracksArray.length <= 1) {
      return 0; // Nothing to sort
    }

    // Sort tracks by numeric ID
    tracksArray.sort((a, b) => {
      const aId = Number(a.id);
      const bId = Number(b.id);

      // Handle non-numeric IDs by placing them at the end
      if (Number.isNaN(aId) && Number.isNaN(bId)) return 0;
      if (Number.isNaN(aId)) return 1;
      if (Number.isNaN(bId)) return -1;

      // Sort numerically
      return descending ? bId - aId : aId - bId;
    });

    // Rebuild the tracks map with the sorted array to maintain order
    this._tracks = new Map(tracksArray.map(track => [track.id, track]));

    return tracksArray.length;
  }

  /**
   * Gets the current scene at the given time
   * @param time Time in seconds
   * @returns The scene containing the given time, or undefined if no scene found
   */
  getCurrentScene(
    time: number,
  ): { scene: Scene; startTime: number; endTime: number } | undefined {
    if (this._scenes.length === 0) {
      return undefined;
    }

    const engine = new LayoutEngine();

    for (const scene of this._scenes) {
      const startTime = engine.resolveAnchor(scene.anchorStart, this);
      const endTime = engine.resolveAnchor(scene.anchorEnd, this);

      if (time >= startTime && time < endTime) {
        return { scene, startTime, endTime };
      }
    }

    return undefined;
  }

  private adjustCoupledElementsToTrackBounds(elements: readonly Element[]): {
    elements: Element[];
    bounds: CoupledElementBounds;
  } {
    const bounds: CoupledElementBounds = {};
    const adjusted = elements.map(el => {
      if (!Element.isLogoElement(el) || !el.isCoupledToTrack()) return el;

      const coupledTrack = this.tracks.find(t => t.id === el.coupledTrackId);
      if (!coupledTrack) return el;

      // Calculate track bounds
      let trackStart = Infinity;
      let trackEnd = -Infinity;

      for (const trackEl of coupledTrack.elements) {
        trackStart = Math.min(trackStart, trackEl.time);
        trackEnd = Math.max(trackEnd, trackEl.time + trackEl.durationSeconds);
      }

      if (!isFinite(trackStart) || !isFinite(trackEnd)) return el;

      const duration = trackEnd - trackStart;
      bounds[el.id] = { time: trackStart, duration };

      // Adjust logo to track bounds using internal method to preserve coupling
      const elCopy = el.clone();
      elCopy.updateCoupledPosition(trackStart, duration);
      return elCopy;
    });
    return { elements: adjusted, bounds };
  }

  /**
   * Reconciles in-memory coupled elements (e.g. logos coupled to the
   * transcript track) with the bounds emitted by serialization
   * (`tempEnhancements.coupledElementBounds`). Mutates `LogoElement`s via
   * `updateCoupledPosition` directly — not through a command — because this
   * is a derived sync, not a user action, and must not enter the undo/redo
   * stack or re-fire observer fan-out.
   */
  reapplyCoupledElementBounds(bounds: CoupledElementBounds | undefined): void {
    if (!bounds) return;
    for (const el of this.allElements) {
      if (!Element.isLogoElement(el)) continue;
      const target = bounds[el.id];
      if (!target) continue;
      if (el.time === target.time && el.durationSeconds === target.duration) {
        continue;
      }
      el.updateCoupledPosition(target.time, target.duration);
    }
  }
}
